import React, { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { Button } from '@roger/ui-components';

import { listPatientChartImportsPatientsPatientIdChartImportsGet as listPatientChartImports } from '../../services/api/sdk.gen';
import type { ChartImportRunSummary } from '../../services/api/types.gen';
import ImportReportDialog from './ImportReportDialog';
import {
  importReportButtonLabel,
  IMPORT_REPORT_LABEL,
} from './importReportCopy';

interface ImportReportButtonProps {
  patientId: string;
}

/**
 * A run nobody has signed off yet.
 *
 * A refresh preview changed nothing, so there is nothing to review on it and
 * it never makes the button urgent. A `scheduled` run (the nightly pass, with
 * Take Over v2 on) belongs to the sync review, so it never makes this button
 * urgent either. With Take Over v2 off the server never sets it, as on main.
 */
function isOutstanding(run: ChartImportRunSummary): boolean {
  return !run.reviewed_at && run.verb !== 'refresh_preview' && !run.scheduled;
}

/**
 * The way into the import report from the patient header.
 *
 * It stands on the chart for as long as the chart has an import behind it,
 * not for as long as the Imported badge is showing. Hiding the badge says
 * "care is established here"; it does not say the import was read. Those are
 * different claims and only one of them is this button's business.
 *
 * The button carries the count of work waiting in Roger so a reader learns
 * there is nothing to do without opening anything. Nothing imported, or the
 * list could not be read: no control at all. A dead button that explains an
 * outage teaches a clinician to distrust the header.
 */
const ImportReportButton: React.FC<ImportReportButtonProps> = ({
  patientId,
}) => {
  const [runs, setRuns] = useState<ChartImportRunSummary[]>([]);
  const [reviewItemsOpen, setReviewItemsOpen] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const { data } = await listPatientChartImports({
          path: { patient_id: patientId },
          throwOnError: true,
        });
        if (!cancelled) {
          setRuns(data?.runs ?? []);
          setReviewItemsOpen(data?.review_items_open ?? 0);
        }
      } catch (err) {
        // Silent by design: the chart itself is not degraded by an
        // unreadable run list, and a toast here would fire on every patient.
        console.error('Failed to load the chart import runs', err);
        if (!cancelled) setRuns([]);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  if (runs.length === 0) return null;

  // Every unreviewed run's work, added up. A chart can carry an import and a
  // later refresh at once; counting only the newest would let a zero-action
  // refresh hide five outstanding items behind a plain label.
  const outstanding = runs.filter(isOutstanding);
  const outstandingActions = outstanding.reduce(
    (total, run) => total + (run.counts.action ?? 0),
    0
  );

  // Items a refresh left for a person to settle count as waiting work too,
  // whether or not the run that wrote them was marked reviewed.
  const urgent = outstanding.length > 0 || reviewItemsOpen > 0;

  return (
    <>
      <Button
        variant={urgent ? 'default' : 'outline'}
        size='sm'
        onClick={() => setOpen(true)}
        data-testid='import-report-button'
      >
        <FileText className='h-3.5 w-3.5 mr-1.5' aria-hidden='true' />
        <span className='whitespace-nowrap'>
          {urgent
            ? importReportButtonLabel(
                outstanding.length > 0 ? outstandingActions : 0,
                reviewItemsOpen
              )
            : IMPORT_REPORT_LABEL}
        </span>
      </Button>
      <ImportReportDialog
        patientId={patientId}
        runs={runs}
        open={open}
        onOpenChange={setOpen}
        onRunsChanged={setRuns}
        onReviewItemsOpenChanged={setReviewItemsOpen}
      />
    </>
  );
};

export default ImportReportButton;
